import {
  Component,
  OnInit,
  ViewEncapsulation,
  OnDestroy,
  Injector,
} from '@angular/core';
import { ColumnList } from '../../constants/column-list.const';
import { BaseGridComponent } from '../../base-grid/base-grid.component';
import { Subscription } from 'rxjs';
import { SelectionResults } from '../../../models/selection-results/selection-results.model';
import { ValidSize } from '../../../models/selection-results/valid-size.model';
import { ZeroToDashPipe } from '../../../../ecaps-core/pipes/custom-pipes.pipe';

@Component({
    selector: 'doas-rve-grid',
    templateUrl: '../../base-grid/base-grid.component.html',
    styleUrls: ['../../base-grid/base-grid.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DoasRveGridComponent
  extends BaseGridComponent
  implements OnInit, OnDestroy
{
  private selectionResultsSubscription: Subscription;

  constructor(protected injector: Injector) {
    super(injector);

    this.showSizeCompare = false;
  }

  ngOnInit() {
    this.columnList = ColumnList.getColumns(
      'relativeCost',
      'coolingLat',
      'totalCoolingCap',
      'heatingLatDb',
      'heatingCap',
      'mcaMop',
      'weight',
      'dimensions',
      'supplyOutletSones',
      'supplyOutletDba',
      'inletSones',
      'exhaustInletSoundDba',
      'ashpLeavingAirDryBulbHeat',
      'ashpHeatingCapacity',
      'erMedia',
      'erDevice',
      'exhaustAirTransferRatio',
      'err'
    );

    const exhaustInletSones = this.columnList.find(
      (item) => item.key === 'inletSones'
    );

    exhaustInletSones.text = 'Exhaust Inlet Sones';

    this.setVisibleColumns([
      'relativeCost',
      'coolingLat',
      'totalCoolingCap',
      'heatingLatDb',
      'heatingCap',
      'mcaMop',
      'weight',
      'dimensions',
    ]);

    this.selectionResultsSubscription = this.selectionResultsReceived.subscribe(
      (selectionResults: SelectionResults) => {
        const newData = [];

        selectionResults.validSizes.forEach((size) => {
          newData.push(this.validSizeToDSRow(size));
        });

        this.dataSource.data = newData;
      }
    );

    this.updateColumnHelp();
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.selectionResultsSubscription.unsubscribe();
  }

  private validSizeToDSRow(size: ValidSize) {
    const zeroDash = new ZeroToDashPipe();

    const rowData = {
      rank: size.sorting.recommendedOrder,
      name: size.name,
      relativeCost: size.pricing.relativePrice,
      coolingLat: `${zeroDash.transform(
        size.outputs.coolingModel.leavingAirDryBulb,
        '1.1-1'
      )} /
        ${zeroDash.transform(
          size.outputs.coolingModel.leavingAirDewPoint,
          '1.1-1'
        )}`,
      totalCoolingCap: size.outputs.coolingModel.coolingCapacity,
      heatingLatDb: size.outputs.coolingModel.selectedCoolingModelType
        .toLowerCase() === 'airsourceheatpump'
        ? size.outputs.heatingModel.ashpLeavingDryBulb
        : size.outputs.heatingModel.leavingDryBulb,
      heatingCap: `${zeroDash.transform(
        size.outputs.heatingModel.heatingCapacity,
        '1.0-0',
        size.outputs.heatingModel.selectedHeatingModelType
          .toLowerCase()
          .trim() === 'electric'
          ? 'kW'
          : 'MBH'
      )}`,
      mcaMop: `${zeroDash.transform(
        size.outputs.fanModel.minimumCircuitAmps,
        '1.1-1'
      )} /
        ${zeroDash.transform(
          size.outputs.fanModel.maximumOvercurrentProtection,
          '1.0-1'
        )}`,
      weight: size.pricing.weight,
      dimensions: `${zeroDash.transform(
        size.outputs.housingWheelModel.dimensions.length,
        '1.0-0'
      )} x
        ${zeroDash.transform(
          size.outputs.housingWheelModel.dimensions.width,
          '1.0-0'
        )} x
        ${zeroDash.transform(
          size.outputs.housingWheelModel.dimensions.height,
          '1.0-0'
        )}`,
      supplyOutletSones:
        size.outputs.fanModel.sound.supplySound.outletSoundSones,
      supplyOutletDba: size.outputs.fanModel.sound.supplySound.outletSoundDba,
      inletSones: size.outputs.fanModel.sound.exhaustSound.inletSoundSones,
      exhaustInletSoundDba:
        size.outputs.fanModel.sound.exhaustSound.inletSoundDba,
      ashpLeavingAirDryBulbHeat: !!size.outputs.coolingModel
        .airSourceHeatPump
        ? size.outputs.coolingModel.airSourceHeatPump
            .ashpLeavingAirDryBulbHeat
        : 0,
      ashpHeatingCapacity: !!size.outputs.coolingModel.airSourceHeatPump
        ? size.outputs.coolingModel.airSourceHeatPump.ashpHeatingCapacity
        : 0,
      erDevice:
        size.outputs.housingWheelModel.energyRecoveryPerformance
          .energyRecoveryTypeActual,
      erMedia:
        size.outputs.housingWheelModel.energyRecoveryPerformance
          .energyRecoveryMedia,
      exhaustAirTransferRatio:
        size.outputs.housingWheelModel.energyRecoveryPerformance
          .exhaustAirTransferRatio,
      err: size.outputs.housingWheelModel.energyRecoveryPerformance
        .totalOutdoorEfficiencySummer,
      stable: size.stability.stable,
      sizeData: size,
    };

    return rowData;
  }
}
